/**
 * Clasifica la respuesta cruda de un envío de prueba de WhatsApp en uno de cuatro estados.
 *
 * Es LA lectura compartida de verdad entre las dos pantallas de prueba — el "Enviar prueba" de
 * WhatsAppConexionPage (`resultadoPruebaEnvio`, texto corto de una línea) y el "Probar en mi
 * WhatsApp" de VistaPreviaMensajes (`resultadoPruebaCampana`, texto más largo que menciona el
 * número). Antes VistaPreviaMensajes tenía su propia copia INLINE de esta misma clasificación
 * (correcta, pero sin test) en vez de llamar a esta función — exactamente la configuración (dos
 * lecturas separadas de la misma respuesta) que dejó reintroducir el falso verde del 422 una vez
 * (ver el commit que arregló ese bug). Las dos pantallas arman su PROPIO texto —necesitan
 * redacciones distintas— pero las dos LEEN la respuesta acá, así que el guard del 422 vive en un
 * solo lugar y está cubierto por tests.
 *
 * El guard del 422 va SIEMPRE primero, antes de tocar `r.data`, por esto:
 *   - `req()` (api.ts) NO tira excepción con un 422 a propósito: lo devuelve entero para que la UI
 *     pueda leer el `code` y decidir qué pantalla mostrar.
 *   - `fail()` (activa.controller.ts) deja el mensaje en el NIVEL RAÍZ de la respuesta
 *     (`{ success:false, error, code }`), nunca bajo `data`.
 * Sin el guard, cualquier lectura tipo `r.data?.ok !== false` ve `r.data` undefined en un 422 y
 * el default (`!== false` → true) dice "salió" sobre un envío que el backend rechazó (ej: un
 * número en la lista de no-contactar). Ese es el falso verde que este archivo blinda: hay un test
 * de mutación en resultadoPrueba.test.ts que rompe el guard a propósito para probar que sin él
 * este caso se pone rojo.
 */
export type EstadoPrueba =
  | { kind: "rechazado"; error?: string } // 422/400+: `fail()` del backend, o el .catch de quien llama
  | { kind: "no-salio"; error?: string }  // 2xx pero el propio backend marcó ok:false
  | { kind: "simulado" }                  // 2xx, ok, pero transporte simulado: no salió a ningún teléfono
  | { kind: "enviado" };                  // 2xx, ok, transporte real

export function leerResultadoPrueba(r: any): EstadoPrueba {
  if (r?.success === false || r?.status >= 400) {
    return { kind: "rechazado", error: r?.error };
  }
  // Acá abajo `r` ya es una respuesta 2xx real (o el objeto que arma el `.catch` de quien llama
  // ante un error de red, con la misma forma bajo `data`). En modo simulado `r.data.ok` da true
  // igual —el transporte simulado "funciona" sin tocar un teléfono real—, así que mirar sólo `ok`
  // decía "✓ enviado" sobre un mensaje que no salió a ningún lado. `transporte` es la señal de si
  // hubo WhatsApp real del otro lado.
  const simulado = r?.data?.transporte === "simulated";
  const salio = r?.data?.ok !== false;
  if (!salio) return { kind: "no-salio", error: r?.data?.error };
  if (simulado) return { kind: "simulado" };
  return { kind: "enviado" };
}

/** Texto corto de una línea para WhatsAppConexionPage ("Enviar prueba" por línea). */
export function resultadoPruebaEnvio(r: any): string {
  const e = leerResultadoPrueba(r);
  switch (e.kind) {
    case "rechazado":
    case "no-salio":
      return "✗ " + (e.error || "error");
    case "simulado":
      return "⚠ modo simulado: no salió a tu teléfono";
    case "enviado":
      return "✓ enviado";
  }
}

/**
 * Texto para "Probar en mi WhatsApp" de VistaPreviaMensajes: a diferencia de `resultadoPruebaEnvio`
 * (una línea de estado corta), acá el mensaje menciona el número al que se mandó y usa una
 * redacción más larga por caso — por eso es una función separada y no sólo un `switch` sobre el
 * string de la otra, pero LEE la respuesta con `leerResultadoPrueba`, la misma clasificación.
 */
export function resultadoPruebaCampana(r: any, numero: string): { ok: boolean; msg: string } {
  const e = leerResultadoPrueba(r);
  switch (e.kind) {
    case "rechazado":
      return { ok: false, msg: e.error || "No se pudo mandar la prueba" };
    case "no-salio":
      return { ok: false, msg: e.error || "WhatsApp rechazó el mensaje: no salió." };
    case "simulado":
      return { ok: false, msg: "WhatsApp está en modo de prueba: el mensaje NO salió a tu teléfono. Conectá un WhatsApp real para probar de verdad." };
    case "enviado":
      return { ok: true, msg: `Mandado a ${numero}. Miralo en tu teléfono.` };
  }
}

/**
 * ¿Ninguna línea de WhatsApp está conectada de verdad? `conectada` es el campo que expone
 * GET /activa/lines reflejando `waManager.isConnected`: no hay que inferirlo de `estado`, que es
 * sólo lo último que quedó escrito en la base y puede ir atrasado respecto de la sesión viva.
 */
export function sinNingunaLineaConectada(lines: any): boolean {
  return !(lines || []).some((l: any) => l?.conectada);
}
